import { Controller } from 'stimulus'
import { showLoader, hideLoader } from '../utils/loader'

// Single key for storing filters - only one page's filters at a time
const FILTERS_STORAGE_KEY = 'admin_table_filters'

export default class extends Controller {
  static targets = ["tableBody", "pagination", "pageInput", "perInput"]

  connect() {
    this.timeout = null
    this.restoreFiltersFromStorage()
    this.updateDateRangeDisplays()

    setTimeout(() => {
      this.updateSortArrows()
    }, 0)
  }

  // Normalize path by removing trailing slash
  normalizePath(path) {
    return path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path
  }

  storeFiltersInStorage(url) {
    const urlObj = new URL(url, window.location.origin)
    const basePath = this.normalizePath(urlObj.pathname)
    const searchParams = urlObj.search

    if (searchParams && searchParams.length > 1) {
      // Store path + filters as single object
      sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({
        path: basePath,
        filters: searchParams
      }))
    }
  }

  restoreFiltersFromStorage() {
    const currentPath = this.normalizePath(window.location.pathname)
    const currentSearch = window.location.search

    if (!currentSearch || currentSearch === '' || currentSearch === '?') {
      const stored = sessionStorage.getItem(FILTERS_STORAGE_KEY)
      if (stored) {
        const { path, filters } = JSON.parse(stored)
        if (path === currentPath && filters) {
          window.location.href = currentPath + filters
        }
      }
    }
  }

  clearStoredFilters() {
    sessionStorage.removeItem(FILTERS_STORAGE_KEY)
  }

  submitSort(event) {
    event.preventDefault()

    const sortField = event.currentTarget.dataset.sortField
    const form = this.element
    const sortInput = form.querySelector('input[name="q[s]"]')

    if (sortInput) {
      const currentSort = sortInput.value || ''
      const parts = currentSort.split(' ')
      const field = parts[0] || ''
      const direction = parts[1] || ''

      if (field === sortField && direction === 'asc') {
        sortInput.value = `${sortField} desc`
      } else {
        sortInput.value = `${sortField} asc`
      }
    }

    if (this.hasPageInputTarget) {
      this.pageInputTarget.value = 1
    }

    this.submit()
  }

  submitForm(event) {
    clearTimeout(this.timeout)

    if (event.type === 'submit') {
      event.preventDefault()
    }

    const resetPage = () => {
      if (this.hasPageInputTarget) {
        this.pageInputTarget.value = 1
      }
    }

    if (event.type === 'input') {
      const debounceDelay = event.target.dataset.debounce || 300
      this.timeout = setTimeout(() => {
        resetPage()
        this.submit()
      }, parseInt(debounceDelay))
    } else {
      if (['change', 'submit'].includes(event.type)) {
        resetPage()
      }
      this.submit()
    }
  }

  submit() {
    const form = this.element
    const actionUrl = new URL(form.action, window.location.origin)
    const formData = new FormData(form)
    formData.forEach((value, key) => {
      // Always set even if empty to signal cleared filters to the server
      actionUrl.searchParams.set(key, value)
    })

    this.loadUrl(actionUrl.toString())
  }

  navigate(event) {
    const link = event.target.closest('a')
    if (!link || link.classList.contains('disabled')) {
      return
    }

    event.preventDefault()

    if (this.hasPageInputTarget) {
      const linkUrl = new URL(link.href, window.location.origin)
      this.pageInputTarget.value = linkUrl.searchParams.get('page') || ''
    }

    this.submit()
  }

  loadUrl(url) {
    // Show loader
    showLoader()

    fetch(url, {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'text/html'
      },
      credentials: 'same-origin'  // Ensure cookies are sent and received for session persistence
    })
    .then(response => response.text())
    .then(html => {
      // Parse the HTML response
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')

      const newTableBody = doc.querySelector('[data-table-body]')
      if (newTableBody && this.hasTableBodyTarget) {
        this.tableBodyTarget.innerHTML = newTableBody.innerHTML
      }

      const newPagination = doc.querySelector('[data-pagination]')
      const currentPagination = this.hasPaginationTarget ? this.paginationTarget : document.querySelector('.pagination-wrapper[data-pagination]')

      if (currentPagination) {
        if (newPagination) {
          const hasContent = newPagination.innerHTML.trim() !== ''
          const hasDNone = newPagination.classList.contains('d-none')

          currentPagination.innerHTML = newPagination.innerHTML

          if (hasDNone || !hasContent) {
            currentPagination.classList.add('d-none')
            currentPagination.style.display = 'none'
          } else {
            currentPagination.classList.remove('d-none')
            currentPagination.style.display = ''
          }
        } else {
          currentPagination.innerHTML = ''
          currentPagination.classList.add('d-none')
          currentPagination.style.display = 'none'
        }
      }

      const newPageInput = doc.querySelector('[data-page-input]')
      if (newPageInput && this.hasPageInputTarget) {
        this.pageInputTarget.value = newPageInput.value
      }

      // Update browser URL to preserve filters on reload
      window.history.replaceState({}, '', url)

      // Store filters in sessionStorage for persistence across page navigations
      this.storeFiltersInStorage(url)

      this.updateSortArrows()
    })
    .catch(error => console.error('Error:', error))
    .finally(() => {
      // Hide loader
      hideLoader()
    })
  }

  clearDateRange(event) {
    event.preventDefault()

    const form = this.element
    const dateFieldPrefix = event.currentTarget.dataset.dateFieldPrefix

    if (dateFieldPrefix) {
      // Clear hidden fields
      const gteqField = form.querySelector(`input[name="q[${dateFieldPrefix}_gteq]"]`)
      const lteqField = form.querySelector(`input[name="q[${dateFieldPrefix}_lteq]"]`)

      if (gteqField) gteqField.value = ''
      if (lteqField) lteqField.value = ''

      const flatpickrInputs = form.querySelectorAll('[data-controller="flatpickr"]')
      flatpickrInputs.forEach(input => {
        if (input.dataset.dateFieldPrefix === dateFieldPrefix) {
          input.value = ''

          const flatpickrController = this.application.getControllerForElementAndIdentifier(input, 'flatpickr')
          if (flatpickrController && flatpickrController.fp) {
            flatpickrController.fp.clear()
          }
        }
      })

      if (this.hasPageInputTarget) {
        this.pageInputTarget.value = 1
      }
      this.submit()
    }
  }

  updateSortArrows() {
    const form = this.element
    const sortInput = form.querySelector('input[name="q[s]"]')
    const currentSort = sortInput?.value || ''

    let field = ''
    let direction = ''

    if (currentSort.trim()) {
      const parts = currentSort.split(' ')
      field = parts[0] || ''
      direction = parts[1] || ''
    }

    const sortLinks = form.querySelectorAll('[data-sort-field]')
    sortLinks.forEach(link => {
      const linkField = link.dataset.sortField
      const textContent = link.textContent
      const cleanText = textContent.replace(/\s*[▲▼]\s*$/, '')

      if (field && linkField === field) {
        const arrow = direction === 'asc' ? ' ▲' : ' ▼'
        link.textContent = cleanText + arrow
      } else {
        link.textContent = cleanText
      }
    })
  }

  // Sync flatpickr displays with hidden field values after AJAX update
  updateDateRangeDisplays() {
    const form = this.element
    const flatpickrInputs = form.querySelectorAll('[data-controller="flatpickr"]')

    flatpickrInputs.forEach(input => {
      const prefix = input.dataset.dateFieldPrefix
      if (!prefix) return

      const gteqField = form.querySelector(`input[name="q[${prefix}_gteq]"]`)
      const lteqField = form.querySelector(`input[name="q[${prefix}_lteq]"]`)
      const startDate = gteqField?.value
      const endDate = lteqField?.value

      if (startDate && endDate) {
        input.value = `${startDate} to ${endDate}`
      } else if (startDate) {
        input.value = startDate
      } else if (endDate) {
        input.value = endDate
      }
    })
  }

  clearFilters(event) {
    event.preventDefault()

    const form = this.element
    const sortInput = form.querySelector('input[name="q[s]"]')
    const currentSort = sortInput?.value || ''

    form.querySelectorAll("input, select").forEach(el => {
      if (el !== sortInput) el.value = ""
    })

    if (sortInput) sortInput.value = currentSort
    const flatpickrInputs = form.querySelectorAll('[data-controller="flatpickr"]')

    flatpickrInputs.forEach(input => {
      input.value = ""
      const fpController = this.application.getControllerForElementAndIdentifier(input, 'flatpickr')
      if (fpController?.fp) {
        fpController.fp.clear()
      }
    })

    if (this.hasPageInputTarget) this.pageInputTarget.value = 1

    this.clearStoredFilters()
    this.submit()
  }
}
